import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { IconButton } from '~/ui';
import { instance } from '~/utils/api/api';
import { downloadFile } from '~/utils/downloadFile';
import { useToastError } from '~/utils/useToastError';
import DownloadIcon from '~/assets/svg/newSvg/actions/download.svg?react';
import styles from './DashboardExportButton.module.scss';

interface Props {
  startDate: string;
  endDate: string;
  assetsIds: number[];
  accommodationIds: number[];
  organizationIds: number[];
}

const DashboardExportButton = ({
  startDate,
  endDate,
  assetsIds,
  accommodationIds,
  organizationIds,
}: Props) => {
  const { t, i18n } = useTranslation();
  const toastError = useToastError();

  const exportMutation = useMutation(
    ['dashboardExportSummary'],
    async () => {
      const response = await instance.get('admin/dashboard/export-summary', {
        params: {
          locale: i18n.language,
          start_date: startDate,
          end_date: endDate,
          asset_ids: assetsIds,
          accommodation_ids: accommodationIds,
          organization_ids: organizationIds,
        },
        responseType: 'blob',
      });
      return response.data;
    },
    {
      onSuccess: data => {
        const start = dayjs(startDate, 'YYYY-MM-DD HH:mm:ss').format(
          'DD-MM-YYYY'
        );
        const end = dayjs(endDate, 'YYYY-MM-DD HH:mm:ss').format('DD-MM-YYYY');
        downloadFile(
          data,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          `OASIS_свод_${start}_${end}.xlsx`
        );
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  return (
    <IconButton
      onClick={() => exportMutation.mutate()}
      isDisabled={exportMutation.isLoading || !startDate || !endDate}
      className={styles.downloadButton}
    >
      {t('dashboard_export_summary')} <DownloadIcon />
    </IconButton>
  );
};

export default DashboardExportButton;
